import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as domtoimage from 'dom-to-image';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { BaseChartDirective } from 'ng2-charts';
import { ChartService } from '../../services/vertical-bars-chart.service';
import { VerticalBarsKpi } from '../../types';
import { VerticalBarsModalComponent } from './vertical-bars-modal/vertical-bars-modal.component';

type AccessibleChartRow = {
  label: string;
  value: string;
};

let nextVerticalBarsId = 0;

@Component({
  selector: 'app-vertical-bars',
  templateUrl: './vertical-bars.component.html',
  providers: [ChartService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VerticalBarsComponent {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective;

  @Input({ required: true }) set data(v: VerticalBarsKpi) {
    this.data$.set(v);
  }

  @Input() loading: boolean = false;

  @Input() total: number = 0;

  @Output() navigate = new EventEmitter<string>();

  @ViewChild('modalContent') modalContent: ElementRef | undefined;

  // #region     BEGIN ChartService
  readonly chartService$ = inject(ChartService);

  get data$(): WritableSignal<VerticalBarsKpi> {
    return this.chartService$.data$;
  }

  readonly updateChartEffect$ = effect(() => {
    this.chartService$.barChartData$();
    this.chart?.update();
  });
  // #endregion  END ChartService

  readonly chartDescId = `vert-bars-card-desc-${(nextVerticalBarsId += 1)}`;

  readonly chartTableTitleId = `vert-bars-card-table-title-${nextVerticalBarsId}`;

  readonly chartTitle$ = computed(() => this.data$()?.title || 'Bar chart');

  readonly chartValueHeader$ = computed(() => {
    const chartData = this.chartService$.barChartData$();

    return chartData.datasets[0]?.label || this.data$()?.yAxisLabel || 'Value';
  });

  readonly accessibleChartRows$ = computed<AccessibleChartRow[]>(() => {
    const chartData = this.chartService$.barChartData$();
    const labels = chartData.labels ?? [];
    const values = chartData.datasets[0]?.data ?? [];

    return labels.map((label, index) => ({
      label: String(label),
      value: String(values[index] ?? ''),
    }));
  });

  readonly chartDescription$ = computed(() => {
    const title = this.chartTitle$();
    const barCount = this.accessibleChartRows$().length;
    const barLabel = barCount === 1 ? 'bar' : 'bars';

    return `${title} bar chart with ${barCount} ${barLabel}. The data table lists each label and value.`;
  });

  private readonly matDialog = inject(MatDialog);

  openModal(): void {
    this.matDialog.open<VerticalBarsModalComponent, VerticalBarsKpi>(
      VerticalBarsModalComponent,
      {
        width: 'calc(100vw - 1rem)',
        data: this.data$(),
        hasBackdrop: true,
        ariaLabelledBy: 'vert-bars-dialog-title',
        ariaDescribedBy: 'vert-bars-chart-desc',
      },
    );
  }

  downloadImage(): void {
    const contentElement = this.modalContent?.nativeElement as HTMLElement;

    if (contentElement) {
      const options = {
        width: contentElement.offsetWidth,
        height: contentElement.offsetHeight,
        scale: 1.5,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top center',
        },
      };

      domtoimage
        .toPng(contentElement, options)
        .then((dataUrl) => {
          const link = document.createElement('a');
          link.href = dataUrl;
          link.download = 'component-image.png';
          link.click();
        })
        .catch((error) => {
          console.warn('Error', error);
        });
    }
  }

  async downloadXlsx(): Promise<void> {
    const chartData = this.chartService$.barChartData$();

    const data = [];
    const { labels } = chartData;

    const headers = [
      'Label',
      ...chartData.datasets.map((dataset) => dataset.label),
    ];
    data.push(headers);

    labels.forEach((label, index) => {
      const row = [label];
      chartData.datasets.forEach((dataset) => {
        row.push(dataset.data[index] ?? 0);
      });
      data.push(row);
    });

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Chart Data');
    worksheet.addRows(data);
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      'chart.xlsx',
    );
  }

  // Ensure chart resizes correctly when viewport changes without page reload
  @HostListener('window:resize')
  onWindowResize(): void {
    // BaseChartDirective's resize completes automatically, but calling update() guarantees reflow
    this.chart?.update();
  }
}
